/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

// Component imports
import { Col, DatePicker, Form, Input, Modal, Row, Select, Typography } from 'antd';
import is from '../../utils/is';
import { isEmail } from '../../utils/is/isEmail';

const validNumber = /^-?\d*\.{0,1}\d+$/;
const validPhone = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/;

const CustomInputAutomationForm = ({
  automation,
  setAutomation,
  onTrigger
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();

  const inputType = (type, placeholder, entries) => {
    switch (type) {
      case 'plain_text':
        return (
          <Input
            placeholder={placeholder}
          />
        );
      case 'email':
        return (
          <Input
            placeholder={placeholder}
            type="email"
          />
        );
      case 'phone_number':
        return (
          <Input
            stringMode
            type="tel"
            placeholder={placeholder}
            className="w-100"
          />
        );
      case 'number':
        return (
          <Input
            stringMode
            placeholder={placeholder}
            className="w-100"
          />
        );
      case 'date':
        return (
          <DatePicker
            placeholder={placeholder}
            onKeyDown={(e) => e.preventDefault()}
            className="w-100"
          />
        );
      case 'choice':
        return (
          <Select
            showSearch
            placeholder="Select an option"
            filterOption={(input, option) =>
              (option?.key ?? '').toLowerCase().includes(input.toLowerCase())
            }
          >
            {entries.map((entry) => {
              return (
                <Select.Option key={entry.key} value={entry.value}>
                  {entry.key}
                </Select.Option>
              );
            })}
          </Select>
        );
      default:
        break;
    }
  };

  const onTriggerConfirm = (values) => {
    const transformedObject = {};
    // We transform any moment objects into formatted date strings
    for (const [key, value] of Object.entries(values)) {
      transformedObject[key] = moment.isMoment(value) ? moment(value).format() : value?.trim();
    }
    const automationWithInput = {
      ...automation,
      customInputForm: transformedObject
    };
    setAutomation(null);
    onTrigger(automationWithInput);
  };

  if (!automation?.agentInput) {
    return null;
  }

  return (
    <Modal
      open={is.truthy(automation)}
      width={660}
      bodyStyle={{ maxHeight: '65vh', overflowY: 'auto', display: automation?.agentInput && automation?.agentInput?.length ? 'block' : 'none' }}
      centered
      title={automation.name}
      destroyOnClose
      onCancel={() => setAutomation(null)}
      afterClose={() => form.resetFields()}
      okText={t('Trigger Automation')}
      onOk={() => form.submit()}
    >
      <Form
        form={form}
        onFinish={onTriggerConfirm}
      >
        <Typography.Paragraph className="fs-sm mb-5">
          {t('Automation')} "{automation.name}" {t('requires additional information')}.
        </Typography.Paragraph>

        {
          automation.agentInput.map((field, index) => {
            const fieldKey = field.name.trim().toLowerCase().replace(/\s/g, '_');

            return (
              <Row
                key={index}
                gutter={16}
                align="top"
              >
                <Col flex="0 0 200px">
                  <Typography.Text
                  >
                    {field.isRequired ? '*' : ''}{field.name}
                  </Typography.Text>
                </Col>
                <Col flex="1 1 50%">
                  <Form.Item
                    name={fieldKey}
                    validateTrigger={['onChange', 'onBlur']}
                    rules={[
                      {
                        validator: async (_rule, value) => {
                          if (!value || (typeof value === 'string' && !value.trim())) {
                            if (field.isRequired) {
                              throw new Error(t('This field is required'));
                            }
                          }

                          if (field.type === 'email' && value && !isEmail(value.trim())) {
                            throw new Error(t('Please enter a valid email address'));
                          }
                          if (field.type === 'number' && value && !validNumber.test(value.trim())) {
                            throw new Error(t('Please enter a number'));
                          }
                          if (field.type === 'phone_number' && value && !validPhone.test(value.trim())) {
                            throw new Error(t('Please enter a valid phone number'));
                          }
                        }
                      }
                    ]}
                  >
                    {inputType(field.type, field.prompt, field?.entries)}
                  </Form.Item>
                </Col>
              </Row>
            );
          })
        }
      </Form>
    </Modal>
  );
};

CustomInputAutomationForm.propTypes = {
  setAutomation: PropTypes.func,
  automation: PropTypes.object,
  onTrigger: PropTypes.func
};

export default CustomInputAutomationForm;
